import { useEffect } from "react";
import { QueryClient, useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from "@tanstack/react-query";
import { getStudentClaims, postStudentClaims, releaseStudentClaim, type StudentClaim } from "@/api/studentClaims";
import type { User } from "@/js/utils/userUtils";
import useStore from "@/stores";

export type StudentClaimsQueryEnablementInput = {
	isClassActive: boolean;
	isMonitoringClass: boolean | undefined;
};

/** Same conditions as `useStudentClaimsQuery` `enabled` — use for UI that must stay in sync with the query. */
export function computeStudentClaimsQueryEnabled(input: StudentClaimsQueryEnablementInput): boolean {
	return Boolean(input.isClassActive && !input.isMonitoringClass);
}

export type RegularClassroomStudentTilesVisibilityInput = {
	isClassActive: boolean;
	isMonitoringClass: boolean | undefined;
	/**
	 * Must reflect `useStudentClaimsQuery`’s `isFetchedAfterMount` when wiring visibility — do not substitute `isFetched`.
	 * `isFetched` means the query has completed at least once ever (including serving cached data); it can already be
	 * true on the first render while `refetchOnMount: "always"` is still refetching. Use `isFetchedAfterMount` when the
	 * intent is to wait until a fetch has completed after mount; it remains true during later background refetches.
	 */
	studentClaimsFetchedAfterMount: boolean;
};

/**
 * Whether to render student tiles/screens in RegularClassroom. Defers tiles, when the claims query applies, until
 * `studentClaimsFetchedAfterMount` is true (derived from `useStudentClaimsQuery`’s `isFetchedAfterMount`).
 * Avoid basing that gate on `isFetched` alone — see `studentClaimsFetchedAfterMount` JSDoc.
 */
export function computeShowRegularClassroomStudentTiles(input: RegularClassroomStudentTilesVisibilityInput): boolean {
	const studentClaimsFetchEnabled = computeStudentClaimsQueryEnabled({
		isClassActive: input.isClassActive,
		isMonitoringClass: input.isMonitoringClass,
	});
	return Boolean(!studentClaimsFetchEnabled || input.studentClaimsFetchedAfterMount);
}

export function studentClaimsQueryKey(classroomStableId: string): readonly ["studentClaims", string] {
	return ["studentClaims", classroomStableId];
}

/**
 * Claims for students managed in other classes (excludes rows where the claim's class is the current classroom).
 */
export function filterStudentClaimsForOtherClasses(claims: StudentClaim[], currentClassStableId: string): StudentClaim[] {
	return claims.filter((claim) => claim.classId !== currentClassStableId);
}

/** Map claims by `studentId` (QID / `qoriaUserId`) for quick lookup on student rows. */
export function studentClaimsToMapByStudentId(claims: StudentClaim[]): Record<string, StudentClaim> {
	return claims.reduce<Record<string, StudentClaim>>((acc, claim) => {
		acc[claim.studentId] = claim;
		return acc;
	}, {});
}

/**
 * Order students managed in another class (student claims) after all others, preserving relative order
 * within each group. Matches tile logic: requires `qoriaUserId` and a claim with `teacherName`.
 */
export function sortUsersWithOtherClassClaimsLast(users: User[], claimsByStudentId: Record<string, StudentClaim>): User[] {
	const managedHere: User[] = [];
	const managedElsewhere: User[] = [];
	for (const user of users) {
		const qid = user.qoriaUserId;
		if (!qid) {
			continue;
		}
		const claim = claimsByStudentId[qid];
		if (claim?.teacherName) {
			managedElsewhere.push(user);
		} else {
			managedHere.push(user);
		}
	}
	return [...managedHere, ...managedElsewhere];
}

export async function fetchStudentClaimsForClassroom(classroomStableId: string): Promise<StudentClaim[]> {
	try {
		const data = await getStudentClaims(classroomStableId);
		return data?.studentClaims ?? [];
	} catch {
		return [];
	}
}

export type UseStudentClaimsQueryArgs = {
	classroomStableId: string;
};

/**
 * Fetches student-claim rows for the classroom when the class session is active.
 * Configured with `refetchOnMount: "always"`. For gating UI on “ready for this visit” (e.g. before showing student
 * tiles), prefer `isFetchedAfterMount` over `isFetched`: `isFetched` can be true immediately from cache while a
 * mount refetch is still in flight.
 */
export function useStudentClaimsQuery({ classroomStableId }: UseStudentClaimsQueryArgs): UseQueryResult<StudentClaim[]> {
	const queryClient = useQueryClient();
	const isClassActive = useStore((state) => state.classroom.isClassSessionActiveV2(classroomStableId));
	const isMonitoringClass = useStore((state) => state.classroom.getClassroomV2(classroomStableId).session?.isMonitoring);
	const enabled = isClassActive && !isMonitoringClass;
	const query = useQuery({
		queryKey: studentClaimsQueryKey(classroomStableId),
		queryFn: () => fetchStudentClaimsForClassroom(classroomStableId),
		enabled: enabled,
		staleTime: 0,
		refetchOnMount: "always",
	});

	// Drop cached claims when the session is inactive so reopening the class does not flash stale Claim UI.
	useEffect(() => {
		if (!enabled && classroomStableId) {
			void queryClient.removeQueries({ queryKey: studentClaimsQueryKey(classroomStableId) });
		}
	}, [enabled, classroomStableId, queryClient]);

	if (!enabled) {
		return {
			data: [],
			isFetched: true,
			isFetchedAfterMount: true,
			isLoading: false,
			isSuccess: true,
		} as unknown as UseQueryResult<StudentClaim[]>;
	}

	return query;
}

export function useClaimStudentsMutation(classroomStableId: string): UseMutationResult<void, Error, string[]> {
	return useMutation({
		mutationFn: async (studentIds: string[]) => {
			await postStudentClaims(classroomStableId, { students: studentIds });
		},
	});
}

export function useReleaseStudentClaimMutation(classroomStableId: string): UseMutationResult<void, Error, string> {
	return useMutation({
		mutationFn: async (studentId: string) => {
			await releaseStudentClaim(classroomStableId, studentId);
		},
	});
}

export async function invalidateStudentClaimsQuery(queryClient: QueryClient, classroomStableId: string | undefined): Promise<void> {
	if (!classroomStableId) {
		return;
	}
	await queryClient.invalidateQueries({ queryKey: studentClaimsQueryKey(classroomStableId) });
}
